/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import "../styles/parent/ParentHome.css";
import ParentMonthlyReportsSection from "../components/parent/ParentMonthlyReportsSection";

function CalendarMini() {
  return (
    <svg viewBox="0 0 24 24" className="pp-home-mini-icon" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="17" rx="3" />
      <path d="M8 2v4M16 2v4M3 10h18" />
    </svg>
  );
}

function UpdatesMini() {
  return (
    <svg viewBox="0 0 24 24" className="pp-home-mini-icon" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 19V5" />
      <path d="M20 19V9" />
      <path d="M4 7c4 0 5-2 8-2s4 2 8 2v8c-4 0-5-2-8-2s-4 2-8 2z" />
    </svg>
  );
}

function SparkMini() {
  return (
    <svg viewBox="0 0 24 24" className="pp-home-mini-icon" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 3l1.7 5.3L19 10l-5.3 1.7L12 17l-1.7-5.3L5 10l5.3-1.7L12 3z" />
    </svg>
  );
}

function ReportMini() {
  return (
    <svg viewBox="0 0 24 24" className="pp-home-mini-icon" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 3h6l4 4v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h2z" />
      <path d="M9 3v4h6V3" />
      <path d="M8 12h8" />
      <path d="M8 16h5" />
    </svg>
  );
}

function BellMini() {
  return (
    <svg viewBox="0 0 24 24" className="pp-home-mini-icon" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M15 17h5l-1.4-1.4a2 2 0 0 1-.6-1.4V11a6 6 0 1 0-12 0v3.2a2 2 0 0 1-.6 1.4L4 17h5" />
      <path d="M10 21a2 2 0 0 0 4 0" />
    </svg>
  );
}

function ChevronMini() {
  return (
    <svg viewBox="0 0 24 24" className="pp-home-mini-icon pp-home-chevron" fill="none" stroke="currentColor" strokeWidth="2.4">
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

function percentFromAttendance(records = []) {
  if (!records.length) return "0.00";
  const present = records.filter((r) => String(r?.status || "").toLowerCase() === "present").length;
  return ((present / records.length) * 100).toFixed(2);
}

function getLocalDateKey(value = new Date()) {
  try {
    const d = value?.toDate ? value.toDate() : value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return "";

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  } catch {
    return "";
  }
}

function getAttendanceRecordDateKey(record = {}) {
  const direct =
    record.dateKey ||
    record.attendanceDate ||
    record.markedDate ||
    record.registerDate ||
    record.date ||
    "";

  if (direct) {
    const directString = String(direct);
    if (/^\d{4}-\d{2}-\d{2}$/.test(directString)) return directString;
    const parsedDirect = getLocalDateKey(direct);
    if (parsedDirect) return parsedDirect;
  }

  return getLocalDateKey(record.timestamp || record.createdAt || record.updatedAt);
}

function getAttendanceSortTime(record = {}) {
  try {
    const raw =
      record.timestamp ||
      record.createdAt ||
      record.updatedAt ||
      record.date ||
      record.dateKey ||
      record.attendanceDate;

    const d = raw?.toDate ? raw.toDate() : new Date(raw);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  } catch {
    return 0;
  }
}

function getAttendanceStatus(records = []) {
  const todayKey = getLocalDateKey();
  const todaysRecords = records
    .filter((record) => getAttendanceRecordDateKey(record) === todayKey)
    .sort((a, b) => getAttendanceSortTime(b) - getAttendanceSortTime(a));

  if (!todaysRecords.length) {
    return {
      tone: "waiting",
      title: "Not marked yet",
      body: "Today’s attendance has not been marked yet.",
    };
  }

  const latest = todaysRecords[0];
  const status = String(latest?.status || "").trim().toLowerCase();

  if (status === "present") {
    return { tone: "good", title: "Marked present", body: "Today’s attendance has been recorded." };
  }

  if (status === "late") {
    return { tone: "warn", title: "Marked late", body: "Today’s attendance shows a late arrival." };
  }

  if (status === "absent") {
    return { tone: "alert", title: "Marked absent", body: "Today’s attendance shows an absence." };
  }

  return {
    tone: "waiting",
    title: "Attendance pending",
    body: "Attendance will appear here once school records update.",
  };
}

function getChildId(child) {
  return child?.id || child?.studentId || child?.docId || "";
}

function getProfileUid(profile) {
  return profile?.uid || profile?.id || profile?.authUid || "";
}

function formatDate(value) {
  if (!value) return "—";
  try {
    if (value?.toDate) return value.toDate().toLocaleDateString("en-GB");
    return new Date(value).toLocaleDateString("en-GB");
  } catch {
    return String(value);
  }
}

function formatLongDate(value) {
  if (!value) return "—";

  try {
    const raw = value?.toDate ? value.toDate() : new Date(value);

    if (Number.isNaN(raw.getTime())) return String(value);

    return raw.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(value);
  }
}

function normaliseDateOnly(value) {
  if (!value) return "";

  try {
    if (value?.toDate) return value.toDate().toISOString().slice(0, 10);

    const asString = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(asString)) return asString;

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return asString;

    return d.toISOString().slice(0, 10);
  } catch {
    return String(value);
  }
}


function normaliseUpdateCategory(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function resolveUpdateFilterKey(item = {}) {
  const directCategory =
    normaliseUpdateCategory(item?.parentCategory) ||
    normaliseUpdateCategory(item?.notificationCategory) ||
    normaliseUpdateCategory(item?.updateCategory) ||
    normaliseUpdateCategory(item?.category) ||
    normaliseUpdateCategory(item?.module) ||
    normaliseUpdateCategory(item?.parentFeedCategory);

  const itemType =
    normaliseUpdateCategory(item?.type) ||
    normaliseUpdateCategory(item?.kind) ||
    normaliseUpdateCategory(item?.notificationType);

  const source = normaliseUpdateCategory(item?.source || item?.sourceType);
  const explicit = directCategory || itemType;

  if ([
    "behaviour",
    "behavior",
    "behaviour_log",
    "behavior_log",
    "behaviour_update",
    "behavior_update",
  ].includes(explicit)) {
    return "behaviour";
  }

  if (["report", "reports", "monthly_report", "attendance_report"].includes(explicit)) {
    return "reports";
  }

  if (["attendance", "attendance_update", "absence", "absence_report"].includes(explicit)) {
    return "attendance";
  }

  if (["class", "class_update"].includes(explicit)) {
    return "class";
  }

  if ([
    "broadcast",
    "announcement",
    "school_update",
    "school_updates",
  ].includes(explicit) || source === "broadcast") {
    return "updates";
  }

  const title = String(item?.title || item?.headline || item?.heading || item?.subject || "").toLowerCase();
  const body = String(item?.body || item?.message || item?.description || item?.content || "").toLowerCase();
  const hay = `${title} ${body}`;

  if (hay.includes("behaviour") || hay.includes("behavior") || hay.includes("kindness") || hay.includes("points")) {
    return "behaviour";
  }

  if (hay.includes("report") || hay.includes("pdf") || hay.includes("monthly")) {
    return "reports";
  }

  if (hay.includes("attendance") || hay.includes("late") || hay.includes("absent")) {
    return "attendance";
  }

  if (hay.includes("class")) {
    return "class";
  }

  return "updates";
}

function getAbsenceDateRange(report) {
  if (!report) return "—";

  if (report.dateRangeLabel) {
    return String(report.dateRangeLabel);
  }

  const start =
    report.startDate ||
    report.fromDate ||
    report.date ||
    report.dateKey ||
    report.createdAt;

  const end =
    report.endDate ||
    report.toDate ||
    report.untilDate ||
    report.startDate ||
    report.date ||
    report.dateKey;

  const startKey = normaliseDateOnly(start);
  const endKey = normaliseDateOnly(end);

  if (startKey && endKey && startKey !== endKey) {
    return `${formatLongDate(start)} – ${formatLongDate(end)}`;
  }

  return formatDate(start || end);
}

function getAbsenceDurationLabel(report) {
  if (!report) return "";

  const explicitDays = Number(report.durationDays || report.days || 0);

  if (explicitDays > 1) {
    return `${explicitDays} days`;
  }

  const startKey = normaliseDateOnly(report.startDate || report.date || report.dateKey);
  const endKey = normaliseDateOnly(report.endDate || report.toDate || report.untilDate);

  if (startKey && endKey && startKey !== endKey) {
    const start = new Date(`${startKey}T00:00:00`);
    const end = new Date(`${endKey}T00:00:00`);
    const diff = Math.round((end - start) / 86400000) + 1;

    if (diff > 1) return `${diff} days`;
  }

  return "1 day";
}

function formatReason(value) {
  return String(value || "Absence report").replaceAll("_", " ");
}

function getAbsenceTone(status) {
  const s = String(status || "submitted").toLowerCase();

  if (s === "approved" || s === "resolved") {
    return { label: "Approved", bg: "#dcfae6", color: "#027a48" };
  }

  if (s === "rejected") {
    return { label: "Rejected", bg: "#fee4e2", color: "#b42318" };
  }

  if (s === "reviewing") {
    return { label: "Reviewing", bg: "#dbeafe", color: "#175cd3" };
  }

  return { label: "Submitted", bg: "#fef0c7", color: "#b54708" };
}

export default function ParentHome() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reportsRef = useRef(null);

  const {
    profile,
    activeChild,
    linkedChildren = [],
    updates = [],
    latestVisibleUpdate = null,
    attendanceRecords = [],
    unreadCount = 0,
    schoolName: resolvedSchoolName = "",
    setActiveChild,
    isOnline = true,
    offlineMessage = "You are offline. Please reconnect and try again.",
  } = useOutletContext() || {};

  const [absenceReports, setAbsenceReports] = useState([]);
  const [absenceLoading, setAbsenceLoading] = useState(false);

const [expandedAbsenceStatus, setExpandedAbsenceStatus] = useState("");

  const parentUid = getProfileUid(profile);
  const schoolName =
    profile?.schoolName ||
    profile?.schoolDisplayName ||
    resolvedSchoolName ||
    activeChild?.schoolName ||
    activeChild?.schoolDisplayName ||
    "Your school";
  const schoolId = profile?.schoolId || activeChild?.schoolId || "";
  const childCount = linkedChildren?.length || 0;
  const activeChildId = getChildId(activeChild);

  const parentName = useMemo(() => {
    const raw = profile?.displayName || profile?.name || profile?.fullName || "Parent";
    return String(raw).split(" ")[0];
  }, [profile]);

  const attendanceRate = useMemo(() => percentFromAttendance(attendanceRecords), [attendanceRecords]);
  const attendanceStatus = useMemo(() => getAttendanceStatus(attendanceRecords), [attendanceRecords]);
  const lastSynced = useMemo(() => {
    return new Date().toLocaleTimeString("en-GB", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }, []);

  const latestUpdate = latestVisibleUpdate || updates?.[0] || null;

  const latestUpdateId = latestUpdate?.id || latestUpdate?.updateId || latestUpdate?.docId || "";
  const latestUpdateTitle = latestUpdate?.title || latestUpdate?.headline || latestUpdate?.subject || "No new school notices";
  const latestUpdateBody = latestUpdate?.body || latestUpdate?.message || latestUpdate?.content || "School and class notices will appear here.";

  const requestedTab = searchParams.get("tab") || "";
  const requestedStudentId = searchParams.get("studentId") || "";

  const loadAbsenceReports = useCallback(async () => {
    if (!schoolId || !parentUid) return;

    try {
      setAbsenceLoading(true);

      const snap = await getDocs(
        query(
          collection(db, "schools", schoolId, "absenceReports"),
          where("parentId", "==", parentUid)
        )
      );

      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((r) => !activeChildId || String(r.studentId || "") === String(activeChildId))
        .sort((a, b) => {
          const ad = a.createdAt?.toMillis?.() || new Date(a.date || a.dateKey || 0).getTime() || 0;
          const bd = b.createdAt?.toMillis?.() || new Date(b.date || b.dateKey || 0).getTime() || 0;
          return bd - ad;
        });

      setAbsenceReports(rows);
    } catch (err) {
      console.warn("Failed to load absence reports:", err);
    } finally {
      setAbsenceLoading(false);
    }
  }, [schoolId, parentUid, activeChildId]);

  useEffect(() => {
    loadAbsenceReports();
  }, [loadAbsenceReports]);

  const latestAbsence = absenceReports[0] || null;
  const latestAbsenceTone = getAbsenceTone(latestAbsence?.status);

  const absenceSummary = useMemo(() => {
    const base = { submitted: 0, reviewing: 0, approved: 0, rejected: 0 };

    absenceReports.forEach((item) => {
      const s = String(item.status || "submitted").toLowerCase();
      if (s === "approved" || s === "resolved") base.approved += 1;
      else if (s === "rejected") base.rejected += 1;
      else if (s === "reviewing") base.reviewing += 1;
      else base.submitted += 1;
    });

    return base;
  }, [absenceReports]);
const absenceSnippet = useMemo(() => {
  if (!expandedAbsenceStatus) return null;

  const matching = absenceReports.filter((item) => {
    const s = String(item.status || "submitted").toLowerCase();

    if (expandedAbsenceStatus === "approved") {
      return s === "approved" || s === "resolved";
    }

    return s === expandedAbsenceStatus;
  });

  const latest = matching[0];

  if (!latest) {
    return {
      title: "No requests here yet",
      body: "There are no absence requests in this status for the selected child.",
    };
  }

  const dateRange = getAbsenceDateRange(latest);
  const durationLabel = getAbsenceDurationLabel(latest);

  return {
    title: `${formatReason(latest.reason)} • ${dateRange}`,
    body:
      latest.decisionNote ||
      latest.adminNote ||
      latest.note ||
      latest.notes ||
      (durationLabel && durationLabel !== "1 day"
        ? `The school has recorded this absence for ${durationLabel}.`
        : "The school has recorded this absence status."),
  };
}, [expandedAbsenceStatus, absenceReports]);


  const markUpdateAsRead = useCallback(
    async (updateId) => {
      if (!updateId || !parentUid) return;
      if (!isOnline) return;

      try {
        await setDoc(
          doc(db, "users", parentUid, "updateReads", updateId),
          {
            updateId,
            schoolId,
            readAt: serverTimestamp(),
            source: "parent-home",
          },
          { merge: true }
        );
      } catch (err) {
        console.warn("Failed to mark update as read:", err);
      }
    },
    [parentUid, schoolId, isOnline]
  );

  const openUnreadUpdates = useCallback(() => {
    navigate(unreadCount ? "/parent/updates?filter=unread" : "/parent/updates");
  }, [navigate, unreadCount]);

  const openLatestUpdate = useCallback(() => {
    if (latestUpdate) {
      const params = new URLSearchParams();
      const filterKey = resolveUpdateFilterKey(latestUpdate);

      if (filterKey) params.set("filter", filterKey);
      if (latestUpdateId) params.set("updateId", latestUpdateId);

      navigate(`/parent/updates?${params.toString()}`);
      return;
    }

    navigate(unreadCount ? "/parent/updates?filter=unread" : "/parent/updates");
  }, [latestUpdate, latestUpdateId, navigate, unreadCount]);

  useEffect(() => {
    if (requestedTab !== "reports") return;
    const t = setTimeout(() => {
      reportsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
    return () => clearTimeout(t);
  }, [requestedTab, activeChild]);

  useEffect(() => {
    if (!requestedStudentId || !Array.isArray(linkedChildren) || !linkedChildren.length) return;
    if (typeof setActiveChild !== "function") return;

    const found = linkedChildren.find((child) => String(getChildId(child)) === String(requestedStudentId));
    if (!found) return;

    const currentId = getChildId(activeChild);
    const nextId = getChildId(found);

    if (String(currentId) !== String(nextId)) {
      setActiveChild(found);
    }
  }, [requestedStudentId, linkedChildren, setActiveChild, activeChild]);

  const childName = activeChild?.name || "No child linked";
  const childYear = activeChild?.year || activeChild?.yearGroup || activeChild?.className || "Year group";

  const quickActions = [
    {
      label: "Attendance",
      detail: `${attendanceRate}% recorded`,
      icon: <CalendarMini />,
      onClick: () => navigate("/parent/attendance"),
      tone: attendanceStatus.tone,
    },
    {
      label: "Updates",
      detail: unreadCount ? `${unreadCount} unread` : "All caught up",
      icon: <BellMini />,
      onClick: openUnreadUpdates,
      tone: unreadCount ? "alert" : "good",
    },
    {
      label: "Report absence",
      detail: "Send a quick absence note",
      icon: <SparkMini />,
      onClick: () => navigate("/parent/report-absence"),
      tone: "soft",
    },
    {
      label: "Reports",
      detail: "Open monthly attendance reports",
      icon: <ReportMini />,
      onClick: () => navigate("/parent/reports"),
      tone: "soft",
    },
  ];

  return (
    <div className="pp-parent-main-content pp-home-premium">
      <section className="pp-card pp-home-hero-card">
        <div className="pp-home-hero-glow" />

        <div className="pp-home-hero-top">
          <div className="pp-home-copy-block">
            <p className="pp-home-eyebrow">Family overview</p>
            <h2 className="pp-home-title">Welcome back, {parentName}</h2>
            <div
  style={{
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 14,
  }}
>
  <div
style={{
  padding: "10px 14px",
  borderRadius: 999,
  background: "#eff6ff",
  border: "1px solid #bfdbfe",
  fontSize: 12,
  fontWeight: 700,
  color: "#1d4ed8",
  letterSpacing: "0.02em",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  width: "fit-content",
  boxShadow: "0 4px 12px rgba(37,99,235,0.08)",
}}
  >
    Last synced: Today at {lastSynced}
  </div>

  <div
style={{
  padding: "10px 14px",
  borderRadius: 999,
  background: "rgba(37, 99, 235, 0.08)",
  border: "1px solid rgba(37, 99, 235, 0.12)",
  fontSize: 12,
  fontWeight: 700,
  color: "#1e3a8a",
  letterSpacing: "0.02em",
  display: "flex",
  alignItems: "center",
  gap: 6,
}}
  >
    <>
  <span style={{ opacity: 0.72 }}>✓</span>
  School-verified attendance
</>
  </div>
</div>
            <p className="pp-home-subtitle">
              Everything important for <strong>{childName}</strong> in one calm space.
            </p>
          </div>

          <button type="button" className="pp-home-status-pill" onClick={openUnreadUpdates}>
            <span className="pp-home-status-dot" />
            <span>{unreadCount ? `${unreadCount} new` : "Live"}</span>
          </button>
        </div>

        <div className="pp-home-hero-grid">
          <div className="pp-home-info-tile">
            <span>Child</span>
            <strong>{childName}</strong>
            <small>{childYear}</small>
          </div>

          <div className="pp-home-info-tile">
            <span>School</span>
            <strong>{schoolName}</strong>
            <small>{childCount ? `Children linked to this account: ${childCount}` : "Child linked to this account"}</small>
          </div>
        </div>
      </section>

      <section className="pp-card pp-home-today-card">
        <div className="pp-home-section-head">
          <div>
            <p className="pp-home-eyebrow">Today</p>
            <h3 className="pp-home-section-title">Stay on top of the day</h3>
          </div>
        </div>

        <p className="pp-home-section-copy">
          Attendance, updates, and quick school communication for {childName}.
        </p>

        <div className="pp-home-focus-grid">
          <button type="button" className={`pp-home-focus-tile ${attendanceStatus.tone}`} onClick={() => navigate("/parent/attendance")}>
            <span className="pp-home-focus-icon"><CalendarMini /></span>
            <span className="pp-home-focus-content">
              <strong>{attendanceStatus.title}</strong>
              <small>{attendanceStatus.body}</small>
            </span>
            <span className="pp-home-focus-arrow"><ChevronMini /></span>
          </button>

          <button type="button" className="pp-home-focus-tile update" onClick={openLatestUpdate}>
            <span className="pp-home-focus-icon"><UpdatesMini /></span>
            <span className="pp-home-focus-content">
              <strong>{latestUpdate ? "Latest school update" : "No notices yet"}</strong>
              <small>{latestUpdate ? "Open the latest school or class notice." : "School and class notices will appear here."}</small>
            </span>
            <span className="pp-home-focus-arrow"><ChevronMini /></span>
          </button>
        </div>
      </section>

      <section className="pp-card pp-home-update-card">
        <div className="pp-home-update-top">
          <span className="pp-home-update-icon"><SparkMini /></span>
          <div className="pp-home-update-copy-wrap">
            <p className="pp-home-eyebrow">Absence status</p>
            <h3 className="pp-home-update-title">
              {latestAbsence ? `${latestAbsenceTone.label} absence request` : "No absence reports yet"}
            </h3>
            <p className="pp-home-update-copy">
              {absenceLoading
                ? "Checking latest absence status..."
                : latestAbsence
                  ? `${formatReason(latestAbsence.reason)} • ${getAbsenceDateRange(latestAbsence)}${
                      getAbsenceDurationLabel(latestAbsence) &&
                      getAbsenceDurationLabel(latestAbsence) !== "1 day"
                        ? ` • ${getAbsenceDurationLabel(latestAbsence)}`
                        : ""
                    }`
                  : "When you report an absence, school decisions will appear here."}
            </p>
          </div>

          {latestAbsence ? (
            <span
              className="pp-home-mini-pill"
              style={{
                background: latestAbsenceTone.bg,
                color: latestAbsenceTone.color,
                boxShadow: "none",
              }}
            >
              {latestAbsenceTone.label}
            </span>
          ) : null}
        </div>

{latestAbsence ? (
  <>
    <div className="pp-home-hero-grid" style={{ marginTop: 14 }}>
      {[
        ["submitted", "Submitted", absenceSummary.submitted, "waiting"],
        ["reviewing", "Reviewing", absenceSummary.reviewing, "in progress"],
        ["approved", "Approved", absenceSummary.approved, "accepted"],
        ["rejected", "Rejected", absenceSummary.rejected, "not accepted"],
      ].map(([key, label, count, small]) => (
        <button
          key={key}
          type="button"
          className="pp-home-info-tile"
          style={{
            textAlign: "left",
            cursor: "pointer",
            outline:
              expandedAbsenceStatus === key ? "2px solid #2563eb" : "none",
          }}
          onClick={() =>
            setExpandedAbsenceStatus((current) =>
              current === key ? "" : key
            )
          }
        >
          <span>{label}</span>
          <strong>{count}</strong>
          <small>{small}</small>
        </button>
      ))}
    </div>

    {absenceSnippet ? (
      <div
        style={{
          marginTop: 12,
          padding: "14px 16px",
          borderRadius: 18,
          background: "#eff6ff",
          border: "1px solid #bfdbfe",
          color: "#1e3a8a",
          lineHeight: 1.5,
        }}
      >
        <strong style={{ display: "block", marginBottom: 4 }}>
          {absenceSnippet.title}
        </strong>
        <span>{absenceSnippet.body}</span>
      </div>
    ) : null}
  </>
) : null}

      </section>

      <section className="pp-card pp-home-quick-card">
        <div className="pp-home-section-head">
          <div>
            <p className="pp-home-eyebrow">Quick actions</p>
            <h3 className="pp-home-section-title">Jump straight in</h3>
          </div>
        </div>

        <div className="pp-home-quick-grid">
          {quickActions.map((action) => (
            <button key={action.label} type="button" className={`pp-home-action-card ${action.tone}`} onClick={action.onClick}>
              <span className="pp-home-action-icon">{action.icon}</span>
              <span className="pp-home-action-copy">
                <strong>{action.label}</strong>
                <small>{action.detail}</small>
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="pp-card pp-home-update-card">
        <div className="pp-home-update-top">
          <span className="pp-home-update-icon"><BellMini /></span>
          <div className="pp-home-update-copy-wrap">
            <p className="pp-home-eyebrow">Latest update</p>
            <h3 className="pp-home-update-title">{latestUpdateTitle}</h3>
            <p className="pp-home-update-copy">{latestUpdateBody}</p>
          </div>
          {unreadCount ? <span className="pp-home-mini-pill">{unreadCount} unread</span> : null}
        </div>

        <button type="button" className="pp-home-primary-action" onClick={openLatestUpdate}>
          Open updates
          <ChevronMini />
        </button>
      </section>

      <section ref={reportsRef} className="pp-home-reports-shell">
        <div className="pp-home-section-head pp-home-reports-head">
          <div>
            <p className="pp-home-eyebrow">Monthly reports</p>
            <h3 className="pp-home-section-title">Attendance documents</h3>
          </div>
        </div>

        <ParentMonthlyReportsSection
          schoolId={schoolId}
          selectedChild={activeChild}
          overrideTestMode={false}
          isOnline={isOnline}
          offlineMessage={offlineMessage}
          title={`Monthly attendance report for ${childName}`}
        />
      </section>
    </div>
  );
}